import React, { useEffect, useState } from "react";
import { Spinner, Button } from "react-bootstrap";
import { XCircleFill } from "react-bootstrap-icons";

const STATUS_PHRASES = [
  "Organizing details",
  "Processing visuals",
  "Compiling insights",
  "Checking observations",
];
const STATUS_INTERVAL_MS = 2200;
const STATUS_FADE_MS = 300;

// Shown while AI processes an item submission; mirrors the user's inputs
// (photos, note). Removed once polling reports SUCCESS/FAILURE.
const MAX_VISIBLE_PHOTOS_DESKTOP = 5;
const MAX_VISIBLE_PHOTOS_MOBILE = 2;
const MOBILE_QUERY = "(max-width: 600px)";

// Overview has no badge — return null.
const badgeFor = ({ type, concernLevel }) => {
  if (type === "Info") return { label: "Info", className: "info" };
  if (type === "Limit") return { label: "Limit", className: "limit" };
  if (type === "Issue") {
    if (concernLevel === 2)
      return { label: "Safety Hazard", className: "hazard" };
    if (concernLevel === 1)
      return { label: "Recommendation", className: "recommendation" };
    return { label: "Maintenance", className: "maintenance" };
  }
  return null;
};

const AIProcessingBlock = ({ item, actions }) => {
  const badge = badgeFor(item);
  const photos = item.photoUrls || [];
  // FAILURE (real failure or timed-out) renders the failed (S6) skin: static
  // error icon instead of the spinner, plus Retry + Discard/Cancel. Create
  // fails have no displayMode; edit fails are "inline".
  const failed = item.taskState === "FAILURE";
  const isEditFail = item.displayMode === "inline";

  const [isMobile, setIsMobile] = useState(() =>
    window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const maxVisible = isMobile
    ? MAX_VISIBLE_PHOTOS_MOBILE
    : MAX_VISIBLE_PHOTOS_DESKTOP;
  const extraCount = Math.max(
    0,
    (item.photoCount || photos.length) - maxVisible
  );
  const visible = photos.slice(0, maxVisible);

  const [phraseIdx, setPhraseIdx] = useState(0);
  const [phraseShown, setPhraseShown] = useState(true);
  useEffect(() => {
    const tick = setInterval(() => {
      setPhraseShown(false);
      setTimeout(() => {
        setPhraseIdx((current) => {
          const next = Math.floor(Math.random() * (STATUS_PHRASES.length - 1));
          return next >= current ? next + 1 : next;
        });
        setPhraseShown(true);
      }, STATUS_FADE_MS);
    }, STATUS_INTERVAL_MS);
    return () => clearInterval(tick);
  }, []);

  return (
    <div className={`ai-processing-block${failed ? " is-failed" : ""}`}>
      <div className="ai-processing-block-header">
        {failed ? (
          <XCircleFill className="ai-processing-block-error-icon" />
        ) : (
          <Spinner
            animation="grow"
            size="sm"
            variant="primary"
            className="ai-processing-block-spinner"
          />
        )}
        {failed ? (
          <span className="ai-processing-block-failed-title">
            AI processing couldn&apos;t finish
          </span>
        ) : (
          <>
            <span className="ai-processing-block-title">AI Processing</span>
            <span
              className={`ai-processing-block-status${
                phraseShown ? "" : " is-fading"
              }`}
            >
              {STATUS_PHRASES[phraseIdx]}...
            </span>
          </>
        )}
        {badge && (
          <span
            className={`ai-processing-block-badge ${badge.className}`}
          >
            {badge.label}
          </span>
        )}
      </div>

      {visible.length > 0 && (
        <div className="ai-processing-block-grid">
          {visible.map((url, idx) => {
            const isLastVisible = idx === visible.length - 1;
            const showOverlay = isLastVisible && extraCount > 0;
            return (
              <div
                key={`${item.taskId}-photo-${idx}`}
                className="ai-processing-block-thumb"
              >
                <img src={url} alt="" />
                {showOverlay && (
                  <span className="ai-processing-block-thumb-overflow">
                    +{extraCount}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {item.displayName && (
        <div className="ai-processing-block-note">{item.displayName}</div>
      )}

      {failed && (
        <div className="ai-processing-block-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            className="ai-processing-block-secondary-btn"
            onClick={() =>
              isEditFail
                ? actions?.onCancel?.(item)
                : actions?.onDiscard?.(item)
            }
          >
            {isEditFail ? "Cancel" : "Discard"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="ai-processing-block-retry-btn"
            onClick={() => actions?.onRetry?.(item)}
          >
            Retry
          </Button>
        </div>
      )}
    </div>
  );
};

export default AIProcessingBlock;
